import { useState, useEffect, useCallback } from 'react'
import {
  trackEvent,
  getEvents,
  clearAnalyticsData,
  calculateMetrics,
  calculatePageMetrics,
  AnalyticsEvent,
  FunnelMetrics,
  FunnelPath,
  PageMetrics,
} from '../utils/analytics'

export type DateRange = 'today' | 'last7days' | 'last30days' | 'all_time'

export function useAnalytics() {
  const [events, setEvents] = useState<AnalyticsEvent[]>([])
  const [metrics, setMetrics] = useState<Record<
    FunnelPath,
    FunnelMetrics
  > | null>(null)
  const [pageMetrics, setPageMetrics] = useState<PageMetrics[]>([])
  const [dateRange, setDateRange] = useState<DateRange>('last7days')
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date())

  // Load data from local storage
  const refreshData = useCallback(() => {
    const allEvents = getEvents()
    setEvents(allEvents)
    setLastRefreshed(new Date())
  }, [])

  // Initial load
  useEffect(() => {
    refreshData()
  }, [refreshData])

  // Recalculate metrics when events or date range changes
  useEffect(() => {
    if (events.length === 0) {
      setPageMetrics([])
      return
    }

    const now = Date.now()
    let startDate = 0

    switch (dateRange) {
      case 'today':
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        startDate = today.getTime()
        break
      case 'last7days':
        startDate = now - 7 * 24 * 60 * 60 * 1000
        break
      case 'last30days':
        startDate = now - 30 * 24 * 60 * 60 * 1000
        break
      case 'all_time':
        startDate = 0
        break
    }

    const calculatedMetrics = calculateMetrics(events, startDate, now)
    const calculatedPageMetrics = calculatePageMetrics(events, startDate, now)

    setMetrics(calculatedMetrics)
    setPageMetrics(calculatedPageMetrics)
  }, [events, dateRange])

  // Wrapper for tracking page views
  const trackPageView = useCallback(() => {
    trackEvent('page_view')
  }, [])

  // Wrapper for tracking clicks (CTAs)
  const trackClick = useCallback((metadata?: Record<string, any>) => {
    trackEvent('click', metadata)
  }, [])

  // Wrapper for tracking conversions (Checkout)
  const trackConversion = useCallback((metadata?: Record<string, any>) => {
    trackEvent('conversion', metadata)
  }, [])

  // Reset data
  const resetData = useCallback(() => {
    if (
      window.confirm(
        'Are you sure you want to clear all analytics data? This cannot be undone.',
      )
    ) {
      clearAnalyticsData()
      refreshData()
      setMetrics(null)
      setPageMetrics([])
    }
  }, [refreshData])

  return {
    metrics,
    pageMetrics,
    dateRange,
    setDateRange,
    refreshData,
    resetData,
    lastRefreshed,
    trackPageView,
    trackClick,
    trackConversion,
  }
}
